import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Check, FileText, PoundSterling, ClipboardList, Zap, Target, ArrowRight, Clock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useQSPricing, QS_CHECK_FEATURES } from './QSPriceBadge';
import { SubscriptionBadge } from './SubscriptionBadge';
import './LandingPageV2.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LogoMark = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
    <rect x="3" y="3" width="18" height="18" rx="2"/>
    <path d="M3 9h18M9 21V9"/>
  </svg>
);

const CheckSvg = ({ color = "rgba(255,255,255,.4)" }) => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

export const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const qsPricing = useQSPricing();
  const qsShowLaunch = qsPricing.launch_active && qsPricing.launch_price < qsPricing.standard_price;
  const qsPriceStr = String(qsPricing.price);

  // §10.3 — Welcome-back banner. Fires once per login session if a draft exists.
  const [draftBanner, setDraftBanner] = useState(null);

  useEffect(() => {
    if (!user) return;
    if (sessionStorage.getItem('welcome_back_dismissed')) return;
    axios.get(`${API}/scope/drafts`)
      .then((r) => {
        const drafts = r.data?.drafts || [];
        if (drafts.length > 0) {
          const d = drafts[0];
          setDraftBanner({ draft_id: d.draft_id, address: d.address || 'your last project' });
        }
      })
      .catch(() => { /* silently ignore — banner is optional enhancement */ });
  }, [user]);

  const dismissBanner = () => {
    sessionStorage.setItem('welcome_back_dismissed', '1');
    setDraftBanner(null);
  };

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="landing-v2">
      {/* NAV */}
      <nav className="lv2-nav">
        <div className="lv2-nav-inner">
          <button className="lv2-nav-logo" onClick={() => navigate('/')}>
            <div className="lv2-logo-mark"><LogoMark /></div>
            <div>
              <div className="lv2-wordmark">SCHEDULES OF WORK</div>
              <span className="lv2-nav-sub">RESIDENTIAL REFURB</span>
            </div>
          </button>
          <div className="lv2-nav-links">
            <button onClick={() => scrollTo('how')}>How it works</button>
            <button onClick={() => navigate('/pricing')} data-testid="nav-pricing">Pricing</button>
            <button onClick={() => scrollTo('sow')}>Schedule of Works</button>
            <button onClick={() => navigate('/contact')}>Contact</button>
            <button onClick={() => navigate('/account')} style={{ display: 'inline-flex', alignItems: 'center' }}>
              My Account<SubscriptionBadge compact />
            </button>
          </div>
          <button className="lv2-nav-cta" onClick={() => navigate('/estimate')} data-testid="nav-get-estimate">
            Get estimate <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      </nav>

      {/* §10.3 — Welcome-back banner. Fires once per login session when a draft exists. */}
      {draftBanner && (
        <div
          data-testid="welcome-back-banner"
          style={{
            background: 'linear-gradient(90deg, #EFF6FF 0%, #DBEAFE 100%)',
            borderBottom: '1px solid #BFDBFE',
            padding: '12px 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 16,
            fontSize: 14,
            color: '#1E3A8A',
            flexWrap: 'wrap',
          }}
        >
          <Clock className="w-4 h-4" />
          <span>
            Welcome back — you have an unfinished project for <strong>{draftBanner.address}</strong>.
          </span>
          <button
            onClick={() => navigate(`/scope?draft=${encodeURIComponent(draftBanner.draft_id)}`)}
            data-testid="welcome-back-continue"
            style={{
              background: '#2563EB', color: '#fff', border: 'none', borderRadius: 8,
              padding: '6px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
            }}
          >
            Continue where you left off →
          </button>
          <button
            onClick={dismissBanner}
            data-testid="welcome-back-dismiss"
            style={{
              background: 'transparent', color: '#475569', border: 'none',
              padding: '6px 8px', fontSize: 12, cursor: 'pointer',
            }}
          >Dismiss</button>
        </div>
      )}

      {/* HERO */}
      <section className="lv2-hero" data-testid="hero-section">
        <div className="lv2-hero-glow" />
        <div className="lv2-hero-grid" />
        <div className="lv2-hero-inner">
          <div className="lv2-eyebrow" data-testid="hero-eyebrow">
            <span className="lv2-eyebrow-dot" />
            QS-grade cost intelligence
          </div>
          <h1 className="lv2-hero-h1">
            How much will your refurb cost?
          </h1>
          <p className="lv2-hero-sub">
            Plan, price and define your project before speaking to builders. Enter your property address and get a realistic cost estimate in minutes — no measuring, no account, no QS fees.
          </p>

          <div className="lv2-hero-cta-wrap">
            {/* §7.7 — Free indicative estimate; remove standalone £29/£119 references per Feb 2026 brief */}
            <button className="lv2-btn-hero" onClick={() => navigate('/estimate')} data-testid="hero-cta-btn">
              Get my estimate →
            </button>
            <div className="lv2-hero-secondary">
              Need a contractor-ready scope?{' '}
              <button onClick={() => scrollTo('sow')}>See the Project Pack →</button>
            </div>
          </div>

          <div className="lv2-trust-row">
            {/* §7.9 — no "Adjusted for your location" language (regional multiplier rebranded) */}
            {['RICS-aligned methodology', 'Priced for your area', 'Used by investors across the UK', 'Takes under 3 minutes'].map(t => (
              <div key={t} className="lv2-trust-item">
                <CheckSvg />
                {t}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OLD WAY / WINNING WAY — comparison block (Apr 2026 brand brief).
          Direct, skim-friendly contrast that makes the "plan before you build"
          proposition tangible. Uses neutral red/green, no emojis. */}
      <section
        className="lv2-comparison"
        data-testid="comparison-section"
        style={{
          padding: '64px 24px',
          background: '#fff',
          borderBottom: '1px solid #e2e8f0',
        }}
      >
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: '0.14em',
              color: '#64748B',
              textTransform: 'uppercase',
              textAlign: 'center',
              marginBottom: 14,
            }}
          >
            Why people use SCOPE
          </div>
          <h2
            style={{
              fontSize: 36,
              fontWeight: 800,
              color: '#0F172A',
              textAlign: 'center',
              margin: '0 0 12px',
              lineHeight: 1.1,
              letterSpacing: '-0.01em',
            }}
          >
            Plan before you build
          </h2>
          <p
            style={{
              fontSize: 16,
              color: '#475569',
              textAlign: 'center',
              maxWidth: 660,
              margin: '0 auto 44px',
              lineHeight: 1.55,
            }}
          >
            Most homeowners speak to builders before they know what they actually want done. That's where projects go off the rails.
          </p>

          <div
            className="lv2-compare-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
              gap: 28,
              position: 'relative',
            }}
          >
            {/* ── Without SCOPE — "crossed-out scribble" aesthetic ── */}
            <div
              data-testid="comparison-without"
              style={{
                position: 'relative',
                background: 'linear-gradient(135deg, #FFF8F6 0%, #FEF2F2 100%)',
                border: '1.5px solid #FCA5A5',
                borderRadius: 18,
                padding: '32px 30px 28px',
                boxShadow: '0 1px 2px rgba(190, 18, 60, 0.04), 0 10px 30px -12px rgba(190, 18, 60, 0.18)',
                overflow: 'hidden',
              }}
            >
              {/* Decorative "scribble" strike-through in the corner */}
              <svg aria-hidden width="120" height="120" viewBox="0 0 120 120" style={{ position: 'absolute', top: -20, right: -25, opacity: 0.06, pointerEvents: 'none' }}>
                <path d="M15 100 L100 20" stroke="#B91C1C" strokeWidth="10" strokeLinecap="round" fill="none"/>
                <path d="M28 112 L115 32" stroke="#B91C1C" strokeWidth="10" strokeLinecap="round" fill="none"/>
              </svg>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 10,
                  background: 'linear-gradient(135deg, #FEE2E2, #FCA5A5)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: 'inset 0 -2px 0 rgba(190, 18, 60, 0.15)',
                }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#991B1B" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M4.9 4.9l14.2 14.2"/>
                  </svg>
                </div>
                <div>
                  <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.14em', color: '#B91C1C', textTransform: 'uppercase' }}>
                    Without SCOPE
                  </div>
                  <div style={{ fontSize: 19, fontWeight: 800, color: '#7F1D1D', lineHeight: 1.2, marginTop: 2, letterSpacing: '-0.01em' }}>
                    The expensive way
                  </div>
                </div>
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {[
                  'Three quotes that vary by 40%',
                  'Hidden costs surface mid-project',
                  'Disputes over what the price covered',
                  'Spreadsheets and guesswork on a Sunday night',
                  'No way to compare quotes like-for-like',
                  'Builders define your scope for you',
                ].map((t) => (
                  <li key={t} style={{
                    display: 'flex', alignItems: 'flex-start', gap: 12,
                    fontSize: 14, color: '#7F1D1D', lineHeight: 1.55,
                    marginBottom: 10,
                    textDecoration: 'line-through',
                    textDecorationColor: 'rgba(190, 18, 60, 0.35)',
                    textDecorationThickness: '1.5px',
                  }}>
                    <span aria-hidden style={{
                      flexShrink: 0, width: 20, height: 20, borderRadius: '50%',
                      background: '#fff', color: '#991B1B',
                      border: '1.5px solid #FCA5A5',
                      fontWeight: 800, fontSize: 13,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      lineHeight: 1, marginTop: 1,
                      textDecoration: 'none',
                    }}>×</span>
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* ── vs. divider — floats between cards on desktop ── */}
            <div aria-hidden className="lv2-compare-vs" style={{
              position: 'absolute',
              top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 54, height: 54,
              background: '#0F172A',
              color: '#fff',
              borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 14, fontWeight: 800, letterSpacing: '0.02em',
              boxShadow: '0 6px 22px -6px rgba(15, 23, 42, 0.45), 0 0 0 5px #fff',
              fontFamily: "Georgia, 'Times New Roman', serif",
              fontStyle: 'italic',
              zIndex: 2,
              pointerEvents: 'none',
            }}>
              vs.
            </div>

            {/* ── With SCOPE — "highlighter" aesthetic + subtle checkmark pattern ── */}
            <div
              data-testid="comparison-with"
              style={{
                position: 'relative',
                background: 'linear-gradient(135deg, #F0FDF4 0%, #ECFDF5 55%, #D1FAE5 100%)',
                border: '1.5px solid #34D399',
                borderRadius: 18,
                padding: '32px 30px 28px',
                boxShadow: '0 1px 2px rgba(4, 120, 87, 0.06), 0 14px 40px -14px rgba(4, 120, 87, 0.25)',
                overflow: 'hidden',
              }}
            >
              {/* Decorative check pattern background */}
              <svg aria-hidden width="140" height="140" viewBox="0 0 140 140" style={{ position: 'absolute', top: -10, right: -18, opacity: 0.08, pointerEvents: 'none' }}>
                <path d="M20 72 L55 100 L120 22" stroke="#047857" strokeWidth="14" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
              </svg>
              {/* Recommended ribbon */}
              <div style={{
                position: 'absolute', top: 16, right: 16,
                background: '#064E3B',
                color: '#D1FAE5',
                fontSize: 10, fontWeight: 800, letterSpacing: '0.12em',
                padding: '4px 10px', borderRadius: 999,
                textTransform: 'uppercase',
                boxShadow: '0 2px 6px rgba(6, 78, 59, 0.3)',
              }}>
                Recommended
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 10,
                  background: 'linear-gradient(135deg, #10B981, #059669)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 2px 6px rgba(5, 150, 105, 0.35), inset 0 -2px 0 rgba(0,0,0,0.1)',
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </div>
                <div>
                  <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.14em', color: '#047857', textTransform: 'uppercase' }}>
                    With SCOPE
                  </div>
                  <div style={{ fontSize: 19, fontWeight: 800, color: '#064E3B', lineHeight: 1.2, marginTop: 2, letterSpacing: '-0.01em' }}>
                    The clear-headed way
                  </div>
                </div>
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {[
                  'Quotes that agree, because the scope is fixed',
                  'Costs known up front and fully transparent',
                  'A contractor-ready Schedule of Works in your hands',
                  'A real cost estimate in minutes, not weekends',
                  'Like-for-like tender returns to compare',
                  'You define your project, builders price it',
                ].map((t, i) => (
                  <li key={t} style={{
                    display: 'flex', alignItems: 'flex-start', gap: 12,
                    fontSize: 14, color: '#064E3B', lineHeight: 1.55,
                    marginBottom: 10,
                    fontWeight: i === 0 ? 600 : 500,
                  }}>
                    <span aria-hidden style={{
                      flexShrink: 0, width: 22, height: 22, borderRadius: '50%',
                      background: 'linear-gradient(135deg, #10B981, #059669)',
                      color: '#fff',
                      fontWeight: 800, fontSize: 13,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      lineHeight: 1, marginTop: 1,
                      boxShadow: '0 1px 3px rgba(5, 150, 105, 0.35)',
                    }}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    </span>
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
              {/* Subtle stat callout at the bottom */}
              <div style={{
                marginTop: 18, padding: '12px 14px',
                background: 'rgba(255,255,255,0.6)',
                border: '1px dashed #6EE7B7',
                borderRadius: 10,
                display: 'flex', alignItems: 'center', gap: 10,
              }}>
                <span style={{ fontSize: 22, fontWeight: 800, color: '#064E3B', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }}>
                  10 min
                </span>
                <span style={{ fontSize: 12, color: '#047857', lineHeight: 1.4 }}>
                  from floor plan to contractor-ready documents.
                </span>
              </div>
            </div>
          </div>

          <div style={{ textAlign: 'center', marginTop: 36 }}>
            <button
              onClick={() => navigate('/estimate')}
              data-testid="comparison-cta"
              style={{
                background: '#0F172A',
                color: '#fff',
                border: 'none',
                borderRadius: 10,
                padding: '13px 28px',
                fontSize: 15,
                fontWeight: 700,
                cursor: 'pointer',
                letterSpacing: '-0.01em',
              }}
            >
              Get my estimate →
            </button>
            <div style={{ marginTop: 10, fontSize: 12, color: '#94A3B8' }}>
              No card. No account. 3 minutes.
            </div>
          </div>
        </div>
      </section>


      {/* VALUE PROPS */}
      <section className="lv2-value-props">
        <div className="lv2-vp-grid">
          {[
            { icon: <Target className="w-6 h-6" style={{ color: 'var(--color-navy)' }} />, title: 'Know your numbers', desc: "Not a guess. Built from real project data and refined using current market conditions and your location — aligned with what contractors are actually charging." },
            { icon: <ClipboardList className="w-6 h-6" style={{ color: 'var(--color-navy)' }} />, title: 'Understand the scope', desc: "A clear breakdown of what needs doing, what it should cost by trade and what materials you'll need." },
            { icon: <Zap className="w-6 h-6" style={{ color: 'var(--color-navy)' }} />, title: 'Go in prepared', desc: "Know what's reasonable before a contractor prices your job. The right questions to ask and what to say when the quotes come in." },
          ].map((vp) => (
            <div key={vp.title} className="lv2-vp-card">
              <div className="lv2-vp-icon">{vp.icon}</div>
              <div className="lv2-vp-title">{vp.title}</div>
              <div className="lv2-vp-desc">{vp.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="lv2-how" id="how" data-testid="how-it-works">
        <div className="lv2-how-inner">
          <div className="lv2-section-eyebrow">How it works</div>
          <h2 className="lv2-section-h2">From postcode to cost guide in minutes</h2>
          <div className="lv2-how-steps">
            {[
              { n: 1, title: 'Enter the address', desc: 'We verify the property and look up publicly available data to confirm the floor area. No measuring needed.' },
              { n: 2, title: 'Tell us the project', desc: 'Three taps — property type, scale of works, what\'s included. Add photos for a more accurate result.' },
              { n: 3, title: 'Get your cost guide', desc: 'A cost range reflecting current market conditions for your location, with a trade-by-trade breakdown, materials list and what to do next.' },
              { n: 4, title: 'Go further if needed', desc: 'Add a QS Check, upgrade to a full measured SOW or book a consultation — all from the same platform.' },
            ].map(s => (
              <div key={s.n} className="lv2-how-step">
                <div className="lv2-how-num">{s.n}</div>
                <div className="lv2-how-step-title">{s.title}</div>
                <div className="lv2-how-step-desc">{s.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* QS VALUE */}
      <section className="lv2-qs-value" data-testid="qs-value-section">
        <div className="lv2-qs-grid" />
        <div className="lv2-qs-inner">
          <div>
            <div className="lv2-section-eyebrow" style={{ color: '#93C5FD' }}>Why Schedules of Work</div>
            <h2 className="lv2-qs-h2">QS-grade cost intelligence — without the QS fees</h2>
            <p className="lv2-qs-sub">
              A professionally prepared feasibility estimate costs £500–£1,500 in QS fees. For most residential refurbs that's hard to justify. We've made the same expertise self-serve and free to start — with the option to have a qualified surveyor verify your figures if you need total confidence.
            </p>
            <button className="lv2-btn-primary" onClick={() => navigate('/estimate')} data-testid="qs-cta-btn">
              Get my free estimate <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          <div className="lv2-qs-compare">
            {[
              { label: 'Professional QS feasibility estimate', price: '£500–£1,500', old: true },
              { label: 'Indicative Cost Guide', price: 'Free', highlight: true },
              { label: 'Full Project Pack', price: '£199' },
              { label: 'QS Check — surveyor verified', price: `£${qsPriceStr}` },
              { label: 'Fully managed QS service', price: 'Contact us', small: true },
            ].map((r, i) => (
              <div key={i} className={`lv2-qs-row${r.highlight ? ' highlight' : ''}`}>
                <div className="lv2-qs-label">{r.label}</div>
                <div className={`lv2-qs-price${r.old ? ' old' : ' new'}${r.small ? ' small' : ''}`}>{r.price}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="lv2-pricing" id="pricing" data-testid="pricing-section">
        <div className="lv2-pricing-inner">
          <div className="lv2-pricing-intro">
            <div className="lv2-section-eyebrow">Pricing</div>
            <h2 className="lv2-section-h2">Clear pricing. No subscriptions.</h2>
            <p style={{ fontSize: 15, color: 'var(--lv2-muted)', lineHeight: 1.6 }}>
              Every product is a one-off payment. Start free with an indicative cost guide and upgrade to the Project Pack when you're ready to engage builders.
            </p>
          </div>
          <div className="lv2-pricing-grid">
            {[
              { name: 'Indicative Cost Guide', forText: 'Homeowners · Investors · Quick sense-check', price: 'Free', note: 'Instant · No payment', features: ['Ballpark cost range', 'Trade-by-trade breakdown', 'Materials list', 'What to ask contractors'], cta: 'Get estimate', href: '/estimate', freePrice: true },
              { name: 'Full Project Pack', forText: 'Self-serve · Full document set', price: '199', note: 'One-off · Instant download · 4 documents', features: ['Full measured Schedule of Works', 'Cost Estimate', 'Key Materials Schedule', 'What To Do Next contractor guide'], cta: 'Start Project Pack', href: '/scope', featured: true },
              { name: 'QS Check', forText: 'Surveyor-verified · 2 working days', price: qsPriceStr, comparePrice: qsShowLaunch ? String(qsPricing.standard_price) : null, note: qsShowLaunch ? 'Launch offer · limited time' : 'Add-on to any output', features: QS_CHECK_FEATURES, cta: 'Add QS Check', href: '#' },
              { name: 'Managed Service', forText: 'Architects · Developers · Complex projects', price: 'Contact us', note: 'Bespoke · Full professional QS', features: ['Full professional service', 'Tender process managed', 'Ongoing support', 'Expert QS team'], cta: 'Get in touch', href: '/upload', ghost: true, contactPrice: true },
            ].map((p) => (
              <div key={p.name} className={`lv2-p-card${p.featured ? ' featured' : ''}`}>
                <div className="lv2-p-hdr">
                  <div className="lv2-p-name">{p.name}</div>
                  <div className="lv2-p-for">{p.forText}</div>
                </div>
                <div className="lv2-p-body">
                  <div className={`lv2-p-amount${p.contactPrice || p.freePrice ? ' contact' : ''}`}>
                    {p.contactPrice || p.freePrice ? (
                      p.price
                    ) : p.comparePrice ? (
                      <>
                        <span style={{ fontSize: 18, fontWeight: 500, color: '#94A3B8', textDecoration: 'line-through', marginRight: 8 }}>£{p.comparePrice}</span>
                        <sup>£</sup>{p.price}
                      </>
                    ) : (
                      <><sup>£</sup>{p.price}</>
                    )}
                  </div>
                  <div className="lv2-p-note">{p.note}</div>
                  <div className="lv2-p-features">
                    {p.features.map(f => (
                      <div key={f} className="lv2-p-feature">
                        <span className="lv2-p-tick">
                          <Check className="w-3 h-3" />
                        </span>
                        {f}
                      </div>
                    ))}
                  </div>
                  <button
                    className={`lv2-p-btn${p.ghost ? ' ghost' : ''}`}
                    onClick={() => navigate(p.href)}
                    data-testid={`pricing-${p.name.toLowerCase().replace(/\s+/g, '-')}-btn`}
                  >
                    {p.cta} {p.ghost && <ArrowRight className="w-3 h-3 inline" />}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SOW DETAIL */}
      <section className="lv2-sow-section" id="sow" data-testid="sow-section">
        <div className="lv2-sow-inner">
          <div className="lv2-sow-text">
            <div className="lv2-section-eyebrow">Schedule of Works</div>
            <h2 className="lv2-section-h2">Professional documentation for projects going to tender</h2>
            <p className="lv2-sow-desc">
              Four contractor-ready documents — full measured Schedule of Works, Cost Estimate, Materials Schedule and a contractor-hire guide. QS-grade, room by room, ready for contractors to price against. Available self-serve or fully prepared by a qualified surveyor.
            </p>
            <div className="lv2-sow-features">
              {[
                { icon: <ClipboardList className="w-4 h-4 text-white" />, text: 'Full itemised Schedule of Works by section and trade' },
                { icon: <PoundSterling className="w-4 h-4 text-white" />, text: 'Cost Estimate with regional adjustment' },
                { icon: <FileText className="w-4 h-4 text-white" />, text: 'Key Materials Schedule for procurement and supplier quotes' },
                { icon: <Check className="w-4 h-4 text-white" />, text: 'What To Do Next — questions to ask contractors and how to compare quotes' },
              ].map(f => (
                <div key={f.text} className="lv2-sow-feature">
                  <div className="lv2-sow-feature-icon">{f.icon}</div>
                  <div className="lv2-sow-feature-text">{f.text}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="lv2-sow-card">
            <div className="lv2-sow-card-title">Choose your option</div>
            <div className="lv2-sow-card-sub">Self-serve using your own floor plan, or fully managed by a qualified surveyor.</div>
            <div className="lv2-sow-options">
              <button className="lv2-sow-option clickable" onClick={() => navigate('/scope')} data-testid="sow-selfserve-btn">
                <div className="lv2-sow-option-label">Self-serve — you measure, we generate</div>
                <div className="lv2-sow-option-desc">Upload your floor plan, draw your rooms, select finishes. Full 4-document set generated instantly — built on decades of experience and real project data.</div>
                <div className="lv2-sow-option-price">£199</div>
                <div className="lv2-sow-option-arrow"><ArrowRight className="w-4 h-4" /></div>
              </button>
              <button className="lv2-sow-option clickable" onClick={() => navigate('/upload')} data-testid="sow-managed-btn">
                <div className="lv2-sow-option-label">Fully managed — we do everything</div>
                <div className="lv2-sow-option-desc">Submit your drawings and project brief. A qualified surveyor prepares your full document set professionally.</div>
                <div className="lv2-sow-option-price">Contact us for pricing</div>
                <div className="lv2-sow-option-arrow"><ArrowRight className="w-4 h-4" /></div>
              </button>
            </div>
            <button className="lv2-p-btn" onClick={() => navigate('/scope')} data-testid="sow-start-btn" style={{ width: '100%', padding: '13px', fontSize: 14 }}>
              Start Schedule of Works <ArrowRight className="w-4 h-4 inline" />
            </button>
          </div>
        </div>
      </section>

      {/* FOOTER CTA */}
      <section className="lv2-footer-cta" id="contact" data-testid="footer-cta">
        <h2>Need something bespoke?</h2>
        <p>Our fully managed QS service covers everything — from feasibility through to tender. Talk to us about your project.</p>
        <button className="lv2-btn-primary" onClick={() => navigate('/contact')} data-testid="bespoke-cta">
          Get in touch <ArrowRight className="w-4 h-4" />
        </button>
      </section>

      {/* FOOTER */}
      <footer className="lv2-footer">
        <div className="lv2-footer-inner">
          <div className="lv2-footer-brand">
            <div>SCHEDULES OF WORK · RESIDENTIAL REFURB</div>
            <div className="lv2-footer-tagline" style={{ fontSize: 12, fontWeight: 500, color: 'rgba(255,255,255,.55)', marginTop: 4, letterSpacing: '.01em' }}>
              Plan, price and define your project before speaking to builders.
            </div>
          </div>
          <div className="lv2-footer-links">
            <button onClick={() => navigate('/terms')}>Terms</button>
            <button onClick={() => navigate('/terms')}>Privacy</button>
            <button onClick={() => navigate('/faq')}>FAQ</button>
            <button onClick={() => navigate('/contact')}>Contact</button>
            <button onClick={() => navigate('/account')}>My Account</button>
            <button onClick={() => navigate('/admin')}>Admin</button>
          </div>
        </div>
      </footer>
    </div>
  );
};
